import { useCallback, useEffect, useMemo, useState } from "react"
import { Gauge } from "lucide-react"
import { useUserRoles, useToast } from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { PageHeader, If, EmptyState } from "../../common/ui"
import {
  useFetchLeadScoresQuery,
  useAcknowledgeLeadScoresMutation
} from "../../redux/queries"
import { LeadScoreCategory, LeadScoreQueryParams } from "../../types"
import { getUserBrandId } from "../../common/utils"
import PaginationWrapper from "../../common/ui/pagination/wrapper"
import ScoreSummaryCards from "../../common/ui/lead-scoring/score-summary-cards"
import CategoryFilterBar from "../../common/ui/lead-scoring/category-filter-bar"
import type { AcknowledgedFilter } from "../../common/ui/lead-scoring/category-filter-bar"
import LeadScoreList from "../../common/ui/lead-scoring/lead-score-list"
import BulkActionToolbar from "../../common/ui/lead-scoring/bulk-action-toolbar"

const PAGE_SIZE = 50

const LeadScoringPage = () => {
  const { user, isSuperAdmin, isBankAdmin, isLoanOfficer } = useUserRoles()
  const { pushSuccessToast, pushErrorToast } = useToast()
  const [activeCategory, setActiveCategory] =
    useState<LeadScoreCategory | null>(null)
  const [acknowledgedFilter, setAcknowledgedFilter] =
    useState<AcknowledgedFilter>("new")
  const [page, setPage] = useState(1)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [optimisticAckedIds, setOptimisticAckedIds] = useState<Set<string>>(
    new Set()
  )

  const [acknowledgeScores, { isLoading: isAcknowledging }] =
    useAcknowledgeLeadScoresMutation()

  const baseParams = useMemo<LeadScoreQueryParams>(() => {
    const params: LeadScoreQueryParams = {}
    if (!isSuperAdmin && !isBankAdmin && isLoanOfficer && user?.id) {
      params.loid = user.id
    } else if (!isSuperAdmin && isBankAdmin && user?.id && user?.org) {
      const brandId =
        getUserBrandId(user.org, user.id) ??
        user.org?.brands?.[0]?.id ??
        user?.brand_id
      if (brandId) params.brand_id = brandId
    }
    return params
  }, [user, isSuperAdmin, isBankAdmin, isLoanOfficer])

  // Unfiltered query for global stats (summary cards)
  const { data: statsResponse } = useFetchLeadScoresQuery({
    ...baseParams,
    limit: 1
  })

  // Filtered + paginated query for the list
  const listParams = useMemo<LeadScoreQueryParams>(
    () => ({
      ...baseParams,
      ...(activeCategory ? { category: activeCategory } : {}),
      ...(acknowledgedFilter === "new"
        ? { acknowledged: 0 as const }
        : acknowledgedFilter === "reviewed"
        ? { acknowledged: 1 as const }
        : {}),
      page,
      limit: PAGE_SIZE
    }),
    [baseParams, activeCategory, acknowledgedFilter, page]
  )
  const { data: listResponse, isLoading } = useFetchLeadScoresQuery(listParams)

  const scores = listResponse?.data ?? []
  const pagination = listResponse?.pagination
  const stats = statsResponse?.stats

  // Clear selection when filters or page change
  useEffect(() => {
    setSelectedIds(new Set())
    setOptimisticAckedIds(new Set())
  }, [activeCategory, acknowledgedFilter, page])

  const handleCategoryChange = useCallback(
    (category: LeadScoreCategory | null) => {
      setActiveCategory(category)
      setPage(1)
    },
    []
  )

  const handleAcknowledgedFilterChange = useCallback(
    (filter: AcknowledgedFilter) => {
      setAcknowledgedFilter(filter)
      setPage(1)
    },
    []
  )

  const handleNeedsReviewClick = useCallback(() => {
    setAcknowledgedFilter("new")
    setActiveCategory(null)
    setPage(1)
  }, [])

  const handleAcknowledge = useCallback(
    async (ids: string[]) => {
      setOptimisticAckedIds(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.add(id))
        return next
      })
      setSelectedIds(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.delete(id))
        return next
      })

      try {
        await acknowledgeScores({ ids }).unwrap()
        pushSuccessToast(
          `${ids.length} lead${ids.length > 1 ? "s" : ""} marked as reviewed`
        )
      } catch {
        setOptimisticAckedIds(prev => {
          const next = new Set(prev)
          ids.forEach(id => next.delete(id))
          return next
        })
        pushErrorToast("Failed to update — please try again")
      }
    },
    [acknowledgeScores, pushSuccessToast, pushErrorToast]
  )

  const handleUndoAcknowledge = useCallback(
    async (ids: string[]) => {
      try {
        await acknowledgeScores({ ids, undo: true }).unwrap()
        pushSuccessToast(
          `${ids.length} lead${ids.length > 1 ? "s" : ""} marked as new`
        )
      } catch {
        pushErrorToast("Failed to update — please try again")
      }
    },
    [acknowledgeScores, pushSuccessToast, pushErrorToast]
  )

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(scores.map(s => s.id)))
  }, [scores])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  // PaginationWrapper is 0-indexed
  const pageIndex = page - 1
  const totalPages = pagination?.total_pages ?? 1

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["lead-scoring"]}>
      <div className="min-h-full p-6 bg-white">
        <PageHeader
          title={
            <span className="flex items-center gap-3">
              Scoreboard{" "}
              <span className="text-sm font-normal text-neutral-400">
                (Preview)
              </span>
              {stats?.unacknowledged != null && stats.unacknowledged > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 border border-primary-200 rounded-full">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-pulse-glow absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-400" />
                  </span>
                  <span className="text-xs font-bold text-primary-600">
                    {stats.unacknowledged} new
                  </span>
                </span>
              )}
            </span>
          }
          hasSubtitle
          subtitle="AI-powered intent scoring for your leads"
        />

        <div className="mt-6 space-y-5">
          <ScoreSummaryCards
            stats={stats}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryChange}
            acknowledgedFilter={acknowledgedFilter}
            onNeedsReviewClick={handleNeedsReviewClick}
          />

          <CategoryFilterBar
            activeCategory={activeCategory}
            onChange={handleCategoryChange}
            acknowledgedFilter={acknowledgedFilter}
            onAcknowledgedFilterChange={handleAcknowledgedFilterChange}
            unacknowledgedCount={stats?.unacknowledged}
          />

          <If
            condition={!isLoading && scores.length > 0}
            isLoading={isLoading}
            then={
              <LeadScoreList
                scores={scores}
                selectedIds={selectedIds}
                optimisticAckedIds={optimisticAckedIds}
                onToggleSelect={handleToggleSelect}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                onAcknowledge={id => handleAcknowledge([id])}
                onUndoAcknowledge={id => handleUndoAcknowledge([id])}
              />
            }
            else={
              <EmptyState
                icon={Gauge}
                heading={
                  acknowledgedFilter === "new"
                    ? "All caught up"
                    : "No leads found"
                }
                subHeading={
                  acknowledgedFilter === "new"
                    ? "No new scores to review — check back later"
                    : "Try adjusting your filters or check back later"
                }
              />
            }
          />

          {totalPages > 1 && (
            <PaginationWrapper
              pageIndex={pageIndex}
              pageSize={PAGE_SIZE}
              rowCount={pagination?.total ?? 0}
              totalCount={totalPages}
              disabledPrev={page <= 1}
              disabledNext={page >= totalPages}
              onClickPrev={() => setPage(p => Math.max(1, p - 1))}
              onClickNext={() => setPage(p => Math.min(totalPages, p + 1))}
              setPageIndex={idx => setPage(idx + 1)}
            />
          )}
        </div>

        {selectedIds.size > 0 && (
          <BulkActionToolbar
            selectedCount={selectedIds.size}
            totalCount={scores.length}
            onAcknowledge={() => handleAcknowledge(Array.from(selectedIds))}
            onUndoAcknowledge={() =>
              handleUndoAcknowledge(Array.from(selectedIds))
            }
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            isAcknowledging={isAcknowledging}
            showUndo={acknowledgedFilter === "reviewed"}
          />
        )}
      </div>
    </RequireRole>
  )
}

export default LeadScoringPage
